import { z } from 'zod'

/**
 * TomTom proxy request schemas.
 *
 * CRITICAL (Pitfall 4): use NAMED fields for coordinates. The proxy composes
 * the TomTom URL internally (which is `lat,lng` order); clients must never
 * have to remember the axis convention.
 *
 * All query params arrive as strings; we use `z.coerce` so numeric params
 * parse cleanly at the boundary.
 */

const Latitude = z.coerce.number().min(-90).max(90)
const Longitude = z.coerce.number().min(-180).max(180)
const PositiveSmallInt = z.coerce.number().int().min(1).max(100)

// Positive, capped radius in metres. TomTom caps at 50000 for Fuzzy; we mirror.
const RadiusMetres = z.coerce.number().int().min(1).max(50000)
// ISO 3166-1 alpha-2 (uppercase), comma-separated for multi-country requests.
const CountrySet = z
  .string()
  .regex(/^[A-Z]{2}(?:,[A-Z]{2})*$/, 'ISO 3166-1 alpha-2, comma-sep')
// BCP-47 short form (e.g. `ro`, `en-GB`).
const LanguageTag = z.string().regex(/^[a-z]{2}(?:-[A-Z]{2})?$/)

export const TomTomRouteQuerySchema = z.object({
  fromLat: Latitude,
  fromLng: Longitude,
  toLat: Latitude,
  toLng: Longitude,
  traffic: z.enum(['true', 'false']).default('true'),
})

export const TomTomTrafficQuerySchema = z.object({
  minLat: Latitude,
  minLng: Longitude,
  maxLat: Latitude,
  maxLng: Longitude,
})

export const TomTomGeocodeQuerySchema = z.object({
  query: z.string().min(1).max(200),
  limit: PositiveSmallInt.optional(),
})

/**
 * TomTom autocomplete (Fuzzy Search v2 post Plan 02-03 — D-57/D-59).
 *
 * - `lat`/`lon` optional but mutually required (refine).
 * - `radius` optional metres, capped at 50000 (TomTom Fuzzy cap).
 * - `countrySet` optional ISO 3166-1 alpha-2 uppercase, comma-sep allowed.
 * - `language` optional BCP-47 short form.
 * - `query`/`limit` unchanged (back-compat).
 */
export const TomTomAutocompleteQuerySchema = z
  .object({
    query: z.string().min(1).max(200),
    limit: PositiveSmallInt.optional(),
    lat: Latitude.optional(),
    lon: Longitude.optional(),
    radius: RadiusMetres.optional(),
    countrySet: CountrySet.optional(),
    language: LanguageTag.optional(),
  })
  .refine((v) => (v.lat === undefined) === (v.lon === undefined), {
    message: 'lat and lon must be provided together',
    path: ['lat'],
  })

export type TomTomAutocompleteQuery = z.infer<typeof TomTomAutocompleteQuerySchema>
